// Copyright (c) 2026 杨翔宇（武汉文理学院）
// 物理智能教学智能体平台 (Physics Intelligent Agent)
// 保留所有权利 (All Rights Reserved)。未经著作权人书面许可，
// 不得以任何形式复制、传播、修改或用于商业用途。

import { Suspense, lazy, useRef } from 'react';
import type { HTMLAttributes, RefObject } from 'react';

import { shouldMountOpticalGlass } from '@/rendering/appSurfacePolicy';
import { useAppSurfacePolicy } from '@/rendering/useAppSurfacePolicy';

import { useLiquidGlassTheme } from './useLiquidGlassTheme';

// 玻璃效果层懒加载：子内容始终立即渲染，玻璃是后到的增强（不影响 LCP / jsdom 测试）。
const LensGlassEffect = lazy(() => import('./LensGlassEffect'));
// 加载器同步引入：Suspense 的 fallback 自身不能再挂起（否则整棵子树会被卸载）
import LatticeLoader from './LatticeLoader';

export type LensScene = 'aurora' | 'sunset' | 'ocean' | 'grid';
export type LensMaterial = { refraction: number; blur: number; dispersion: number };

export interface LensGlassRootProps extends HTMLAttributes<HTMLDivElement> {
  /** 缺省按当前主题取材质（八套主题共用同一引擎）。 */
  scene?: LensScene;
  material?: LensMaterial;
  /** 默认 false：尊重 prefers-reduced-motion / 性能模式。 */
  motion?: boolean;
  /** 默认读取设置中的「液态玻璃音效」开关。 */
  sound?: boolean;
  elasticity?: number;
}

/**
 * 批次 8 M6：八套主题统一走 Lens 液态玻璃引擎——容器内所有 [data-glass] 表面
 * 获得真实折射 / 色散 / 弹簧形变；材质与场景按主题调参（见 useLiquidGlassTheme）。
 * 无 WebGL2 / jsdom 下由库自动降级为 CSS 毛玻璃。
 *
 * 批次 8 扩围：根节点即舞台，原生属性（data-testid / role / data-surface-mode / …）
 * 原样透传，便于把既有页面根节点直接换成舞台而不丢语义与测试锚点。
 */
export default function LensGlassRoot({
  scene,
  material,
  motion = false,
  sound,
  elasticity,
  className,
  children,
  ...rest
}: LensGlassRootProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const { material: themeMaterial, scene: themeScene } = useLiquidGlassTheme();
  const surfacePolicy = useAppSurfacePolicy();

  // T04：工作区（以及认证 pending）不创建任何光学舞台——不是隐藏画布，而是
  // 根本不挂 LensGlassEffect、不加载 LatticeLoader、不宣称 webgl-ready。
  if (!shouldMountOpticalGlass(surfacePolicy)) {
    return (
      <div ref={rootRef} className={className} data-lens-glass-root="off" {...rest}>
        {children}
      </div>
    );
  }

  return (
    <div ref={rootRef} className={className} data-lens-glass-root="true" {...rest}>
      <Suspense fallback={<LatticeLoader className="pointer-events-none absolute inset-0" />}>
        <LensGlassEffect
          rootRef={rootRef}
          scene={scene ?? themeScene}
          material={material ?? themeMaterial}
          motion={motion}
          sound={sound}
          elasticity={elasticity}
        />
      </Suspense>
      {children}
    </div>
  );
}

export type LensGlassEffectProps = {
  rootRef: RefObject<HTMLElement>;
} & Omit<LensGlassRootProps, 'children' | 'className'>;
